import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus, Handshake, ClipboardList, Megaphone,
  Phone, Mail, Calendar, FileText, Database,
} from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, Tooltip } from 'recharts';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import FollowUpWidget from '../components/FollowUpWidget';
import { getContacts, getDeals, getActivities, getDashboardMetrics } from '../services/api';
import api from '../services/api';
import type { Deal, Activity as ActivityType } from '../types';

/* ── accent palette ──────────────────────────────────────────────── */
const accents: Record<string, { bg: string; border: string; text: string }> = {
  sage:   { bg: 'rgba(144,185,171,0.08)', border: 'rgba(144,185,171,0.25)', text: '#90b9ab' },
  blue:   { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.25)',  text: '#3b82f6' },
  amber:  { bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.25)',  text: '#fbbf24' },
  purple: { bg: 'rgba(168,85,247,0.08)',  border: 'rgba(168,85,247,0.25)',  text: '#a855f7' },
};

/* ── activity icon mapping ───────────────────────────────────────── */
const activityIcons: Record<string, { icon: typeof Phone; color: string }> = {
  call:    { icon: Phone,          color: '#3b82f6' },
  email:   { icon: Mail,           color: '#a855f7' },
  meeting: { icon: Calendar,       color: '#fbbf24' },
  note:    { icon: FileText,       color: '#90b9ab' },
  task:    { icon: ClipboardList,  color: '#ef4444' },
};

/* ── stage colors for pipeline bar ───────────────────────────────── */
const stageConfig: { key: string; label: string; color: string }[] = [
  { key: 'lead',           label: 'Lead',           color: '#3b82f6' },
  { key: 'prospecting',    label: 'Prospecting',    color: '#06b6d4' },
  { key: 'qualification',  label: 'Qualification',  color: '#8b5cf6' },
  { key: 'qualified',      label: 'Qualified',      color: '#8b5cf6' },
  { key: 'proposal',       label: 'Proposal',       color: '#fbbf24' },
  { key: 'negotiation',    label: 'Negotiation',    color: '#f97316' },
  { key: 'closed_won',     label: 'Won',            color: '#22c55e' },
  { key: 'closed_lost',    label: 'Lost',           color: '#ef4444' },
];

/* ── relative time helper ────────────────────────────────────────── */
function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ── component ───────────────────────────────────────────────────── */
export default function DashboardPage() {
  const navigate = useNavigate();

  const [totalContacts, setTotalContacts] = useState(0);
  const [deals, setDeals] = useState<Partial<Deal>[]>([]);
  const [totalDeals, setTotalDeals] = useState(0);
  const [activities, setActivities] = useState<Partial<ActivityType>[]>([]);
  const [totalActivities, setTotalActivities] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataHealthPct, setDataHealthPct] = useState<number | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      setLoading(true);
      try {
        const [contactsRes, dealsRes, activitiesRes] = await Promise.allSettled([
          getContacts({ limit: '1' }),
          getDeals({ limit: '50' }),
          getActivities({ limit: '10' }),
        ]);

        const contacts = contactsRes.status === 'fulfilled'
          ? (contactsRes.value?.total ?? contactsRes.value?.items?.length ?? 0)
          : 0;
        setTotalContacts(contacts);

        const dealsData = dealsRes.status === 'fulfilled' ? dealsRes.value : null;
        const d = dealsData?.items ?? dealsData ?? [];
        setDeals(Array.isArray(d) ? d : []);
        setTotalDeals(dealsData?.total ?? (Array.isArray(d) ? d.length : 0));

        const activitiesData = activitiesRes.status === 'fulfilled' ? activitiesRes.value : null;
        const a = activitiesData?.items ?? activitiesData ?? [];
        setActivities(Array.isArray(a) ? a : []);
        setTotalActivities(activitiesData?.total ?? (Array.isArray(a) ? a.length : 0));
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();

    // Also try the dedicated analytics endpoint
    getDashboardMetrics().catch(() => {});

    // Load data health
    api.get('/enrichment/stats')
      .then(res => setDataHealthPct(res.data?.overall_pct ?? null))
      .catch(() => {});
  }, []);

  /* ── derived metrics ─────────────────────────────────────────── */
  const activeDeals = deals.filter((d: any) => !['closed_won', 'closed_lost'].includes(d.stage));
  const pipelineValue = activeDeals.reduce((sum: number, d: any) => sum + (d.value || 0), 0);
  // Use totalDeals from API (covers all records), minus closed deals in the fetched page
  const closedInPage = deals.filter((d: any) => ['closed_won', 'closed_lost'].includes(d.stage)).length;
  const activeDealCount = totalDeals > deals.length
    ? totalDeals - closedInPage   // approximate: total minus closed we can see
    : activeDeals.length;         // fallback: use filtered array

  const heroCards = [
    { label: 'Total Contacts', value: totalContacts, accent: 'sage',   fmt: (v: number) => v.toLocaleString() },
    { label: 'Active Deals',   value: activeDealCount, accent: 'blue',   fmt: (v: number) => String(v) },
    { label: 'Pipeline Value',  value: pipelineValue, accent: 'amber',  fmt: (v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}` },
    { label: 'Activities',      value: totalActivities, accent: 'purple', fmt: (v: number) => String(v) },
  ];

  /* ── pipeline stage data ─────────────────────────────────────── */
  const stageCounts: Record<string, number> = {};
  deals.forEach((d: any) => {
    const stage = d.stage || 'discovery';
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
  });
  const pipelineBarData = [
    stageConfig.reduce<Record<string, number>>((acc, s) => {
      acc[s.key] = stageCounts[s.key] || 0;
      return acc;
    }, {}),
  ];

  /* ── quick actions ─────────────────────────────────────────────── */
  const quickActions = [
    { label: 'Add Contact',   icon: UserPlus,      to: '/contacts' },
    { label: 'New Deal',      icon: Handshake,     to: '/pipeline' },
    { label: 'Log Activity',  icon: ClipboardList, to: '/activities' },
    { label: 'New Campaign',  icon: Megaphone,     to: '/campaigns' },
  ];

  if (loading) {
    return (
      <div>
        <Header title="Dashboard" subtitle="Sales pipeline overview" />
        <div className="p-4 md:p-8 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
          <p className="text-sm">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <Header title="Dashboard" subtitle="Sales pipeline overview" />

      <div className="p-4 md:p-8 space-y-6">

        {/* ── Hero Metrics Row ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {heroCards.map((card) => {
            const a = accents[card.accent];
            return (
              <div
                key={card.label}
                className="rounded-xl p-5 transition-all duration-200"
                style={{
                  background: a.bg,
                  border: `1px solid ${a.border}`,
                }}
              >
                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                  {card.label}
                </p>
                <span className="font-display text-3xl" style={{ color: 'var(--text-primary)' }}>
                  {card.fmt(card.value)}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Quick Actions Row ─────────────────────────────────────── */}
        <div className="flex gap-3 overflow-x-auto pb-1">
          {quickActions.map((qa) => {
            const Icon = qa.icon;
            return (
              <button
                key={qa.label}
                onClick={() => navigate(qa.to)}
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm whitespace-nowrap transition-all duration-200"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-primary)',
                  backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturation))',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--netrun-green)';
                  e.currentTarget.style.background = 'rgba(144,185,171,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--glass-border)';
                  e.currentTarget.style.background = 'var(--glass-bg)';
                }}
              >
                <Icon size={16} style={{ color: 'var(--netrun-green)' }} />
                {qa.label}
              </button>
            );
          })}
        </div>

        {/* ── Two Column Layout ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Recent Activity */}
          <Panel title="Recent Activity" noPadding>
            <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
              {activities.length === 0 && (
                <p className="px-5 py-8 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
                  No recent activity. Log your first interaction.
                </p>
              )}
              {activities.slice(0, 7).map((act: any) => {
                const config = activityIcons[act.type] || activityIcons.note;
                const Icon = config.icon;
                return (
                  <div key={act.id} className="flex items-center justify-between px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: `${config.color}15` }}
                      >
                        <Icon size={14} style={{ color: config.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                          {act.subject || 'Activity'}
                        </p>
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>
                          {act.contact_name}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs flex-shrink-0 ml-3" style={{ color: 'var(--text-muted)' }}>
                      {act.created_at ? relativeTime(act.created_at) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Pipeline Overview */}
          <Panel title="Pipeline Overview">
            {deals.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <p className="text-sm">No deals yet. Create your first opportunity.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Stacked bar */}
                <ResponsiveContainer width="100%" height={40}>
                  <BarChart layout="vertical" data={pipelineBarData} barSize={24}>
                    <Tooltip
                      cursor={false}
                      contentStyle={{
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: 8,
                        color: 'var(--text-primary)',
                        fontSize: 12,
                      }}
                    />
                    {stageConfig.map((s) => (
                      <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.color} radius={0} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>

                {/* Legend */}
                <div className="grid grid-cols-2 gap-2">
                  {stageConfig.map((s) => {
                    const count = stageCounts[s.key] || 0;
                    if (count === 0) return null;
                    return (
                      <div key={s.key} className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {s.label}
                        </span>
                        <span className="text-xs font-display ml-auto" style={{ color: 'var(--text-primary)' }}>
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Panel>

        </div>

        {/* ── Intelligence Row ──────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Follow-up Reminders Widget */}
          <FollowUpWidget />

          {/* Data Health Card */}
          <Panel
            title="Data Health"
            action={
              <button
                onClick={() => navigate('/enrichment')}
                className="text-xs"
                style={{ color: 'var(--netrun-green)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Improve →
              </button>
            }
          >
            {dataHealthPct === null ? (
              <div className="flex items-center gap-3 py-2">
                <Database size={28} style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading data health...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Overall ring */}
                <div className="flex items-center gap-5">
                  <div className="relative w-16 h-16 flex-shrink-0">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="14"
                        fill="none"
                        stroke={dataHealthPct >= 80 ? '#22c55e' : dataHealthPct >= 60 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="3"
                        strokeDasharray={`${(dataHealthPct / 100) * 87.96} 87.96`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      className="absolute inset-0 flex items-center justify-center font-display text-sm"
                      style={{ color: dataHealthPct >= 80 ? '#22c55e' : dataHealthPct >= 60 ? '#f59e0b' : '#ef4444' }}
                    >
                      {dataHealthPct.toFixed(0)}%
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {dataHealthPct >= 80 ? 'Contact data is in good shape' :
                       dataHealthPct >= 60 ? 'Some enrichment recommended' :
                       'Significant data gaps detected'}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      Based on email, title, company, LinkedIn, and phone coverage.
                    </p>
                    <button
                      onClick={() => navigate('/enrichment')}
                      className="text-xs mt-2"
                      style={{ color: 'var(--netrun-green)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      View enrichment opportunities →
                    </button>
                  </div>
                </div>

                {/* Quick links */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Reminders', to: '/reminders', color: '#f59e0b' },
                    { label: 'Attribution', to: '/attribution', color: '#a855f7' },
                  ].map(link => (
                    <button
                      key={link.label}
                      onClick={() => navigate(link.to)}
                      className="rounded-lg py-2 text-xs text-center transition-all"
                      style={{
                        background: `${link.color}10`,
                        border: `1px solid ${link.color}25`,
                        color: link.color,
                        cursor: 'pointer',
                      }}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        </div>

      </div>
    </div>
  );
}
